import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Typography,
  CircularProgress,
  TextField,
} from '@material-ui/core';

import { ApplicationState } from '../../../store';
import { fetchDashboardRequest } from '../../../store/modules/admin/actions';
import { Container, CardsRow, StatCard, useStyles } from './DashboardStyles';
import api from '../../../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

interface InsightsCostUser {
  userId: string;
  name: string;
  email: string;
  insightCount: number;
  totalCostUSD: number;
}

interface InsightsCostData {
  month: string;
  totalCostUSD: number;
  totalInsights: number;
  users: InsightsCostUser[];
}

export default function DashboardView() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { dashboard, loading } = useSelector((state: ApplicationState) => state.admin);

  var [costData, setCostData] = useState<InsightsCostData | null>(null);
  var [costLoading, setCostLoading] = useState(false);
  var [costMonth, setCostMonth] = useState(format(new Date(), 'yyyy-MM'));

  useEffect(function() {
    dispatch(fetchDashboardRequest());
  }, [dispatch]);

  useEffect(function() {
    fetchCost(costMonth);
  }, [costMonth]); // eslint-disable-line

  function fetchCost(month: string) {
    setCostLoading(true);
    api.get('/admin/insights-cost?month=' + month)
      .then(function(res) {
        setCostData(res.data);
        setCostLoading(false);
      })
      .catch(function() {
        setCostLoading(false);
      });
  }

  async function handleRecordPayment(userId: string) {
    try {
      var referenceMonth = format(new Date(), 'yyyy-MM');
      await api.post('/admin/payments', {
        userId,
        amount: 0,
        referenceMonth,
        status: 'paid',
      });
      toast.success('Pagamento registrado com sucesso!');
      dispatch(fetchDashboardRequest());
    } catch {
      toast.error('Erro ao registrar pagamento.');
    }
  }

  if (loading) {
    return <CircularProgress style={{ margin: '40px auto', display: 'block' }} />;
  }

  return (
    <Container>
      <CardsRow>
        <StatCard>
          <Typography className={classes.cardTitle}>Total de Usuarios</Typography>
          <Typography className={classes.cardValue}>{dashboard.totalUsers}</Typography>
        </StatCard>
        <StatCard>
          <Typography className={classes.cardTitle}>Ativos</Typography>
          <Typography className={classes.cardValue}>{dashboard.activeUsers}</Typography>
        </StatCard>
        <StatCard>
          <Typography className={classes.cardTitle}>Trial</Typography>
          <Typography className={classes.cardValue}>{dashboard.trialUsers}</Typography>
        </StatCard>
        <StatCard>
          <Typography className={classes.cardTitle}>Sem Pagamento</Typography>
          <Typography className={classes.cardValue}>
            {dashboard.usersNeedingPayment.length}
          </Typography>
        </StatCard>
      </CardsRow>

      <div className={classes.tableContainer}>
        <Typography className={classes.sectionTitle}>
          Usuarios sem pagamento este mes
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell>Acao</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dashboard.usersNeedingPayment.map(function(user) {
              return (
                <TableRow key={user._id}>
                  <TableCell>{user.basicInfo.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {format(new Date(user.createdAt), 'dd/MM/yyyy')}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={function() { handleRecordPayment(user._id); }}
                    >
                      Registrar Pgto
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {dashboard.usersNeedingPayment.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Todos os usuarios ativos estao com pagamento em dia.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Insights AI Cost */}
      <div className={classes.tableContainer} style={{ marginTop: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
          <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
            Custos Insights IA
          </Typography>
          <TextField
            type="month"
            value={costMonth}
            onChange={function(e) { setCostMonth(e.target.value); }}
            size="small"
            variant="outlined"
            style={{ width: '180px' }}
          />
        </div>

        {costLoading && (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <CircularProgress size={28} />
          </div>
        )}

        {!costLoading && costData && (
          <>
            <CardsRow style={{ marginBottom: '16px' }}>
              <StatCard>
                <Typography className={classes.cardTitle}>Total Insights</Typography>
                <Typography className={classes.cardValue}>{costData.totalInsights}</Typography>
              </StatCard>
              <StatCard>
                <Typography className={classes.cardTitle}>Custo Total (USD)</Typography>
                <Typography className={classes.cardValue}>${costData.totalCostUSD.toFixed(4)}</Typography>
              </StatCard>
              <StatCard>
                <Typography className={classes.cardTitle}>Custo Total (BRL)</Typography>
                <Typography className={classes.cardValue}>R$ {(costData.totalCostUSD * 5.7).toFixed(2)}</Typography>
              </StatCard>
            </CardsRow>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="center">Insights</TableCell>
                  <TableCell align="right">Custo (USD)</TableCell>
                  <TableCell align="right">Custo (BRL)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costData.users.map(function(user) {
                  return (
                    <TableRow key={user.userId}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell align="center">{user.insightCount}</TableCell>
                      <TableCell align="right">${user.totalCostUSD.toFixed(4)}</TableCell>
                      <TableCell align="right">R$ {(user.totalCostUSD * 5.7).toFixed(2)}</TableCell>
                    </TableRow>
                  );
                })}
                {costData.users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" style={{ color: '#ffffff55' }}>
                      Nenhum insight gerado neste mes.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </Container>
  );
}
